import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Crown, Star, Gift, LogOut, Award, ShoppingBag, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const tierConfig = {
  free: { label: 'Introductory', color: 'bg-gray-100 text-gray-700', icon: Star },
  silver: { label: 'Silver', color: 'bg-gray-200 text-gray-800', icon: Award },
  gold: { label: 'Gold', color: 'bg-[#D4A574]/20 text-[#C4844A]', icon: Crown },
  platinum: { label: 'Platinum', color: 'bg-[#2C1810] text-[#D4A574]', icon: Crown },
};

const Account = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [pointsInfo, setPointsInfo] = useState(null);

  useEffect(() => {
    if (user === false) navigate('/login');
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.id) {
      fetch(`${API}/api/user/points`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setPointsInfo(d); })
        .catch(() => {});
    }
  }, [user]);

  if (loading || user === null) {
    return <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center pt-24"><div className="animate-pulse text-[#7a6555]">Loading...</div></div>;
  }

  if (!user) return null;

  const tier = tierConfig[user.tier] || tierConfig.free;
  const TierIcon = tier.icon;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#FDF8F0] pt-28 pb-12" data-testid="account-page">
      <SEO title="My Account" />
      <div className="max-w-4xl mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
          {/* Welcome Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Welcome, <span className="text-[#5C7A3A]">{user.name}</span>
              </h1>
              <p className="text-sm text-[#7a6555] mt-1">{user.email}</p>
            </div>
            <Button onClick={handleLogout} variant="outline" className="border-[#E8DFD3] text-[#7a6555] hover:text-red-600 hover:border-red-200 cursor-pointer" data-testid="logout-btn">
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>

          {/* Membership Card */}
          <Card className="mb-6 overflow-hidden border border-[#E8DFD3] shadow-lg">
            <div className="bg-gradient-to-r from-[#2C1810] to-[#4a3020] px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D4A574]/20 rounded-xl flex items-center justify-center">
                    <TierIcon size={20} className="text-[#D4A574]" />
                  </div>
                  <div>
                    <p className="text-xs text-white/60 uppercase tracking-wider">Membership Tier</p>
                    <p className="text-lg font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>{tier.label}</p>
                  </div>
                </div>
                <Link to="/membership">
                  <Button size="sm" className="bg-[#D4A574] hover:bg-[#C4844A] text-white cursor-pointer text-xs">
                    {user.tier === 'platinum' ? 'View Benefits' : 'Upgrade'}
                  </Button>
                </Link>
              </div>
            </div>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-[#FDF8F0] rounded-xl">
                  <Gift size={20} className="mx-auto text-[#5C7A3A] mb-1" />
                  <p className="text-2xl font-bold text-[#2C1810]">{pointsInfo?.points || user.points || 0}</p>
                  <p className="text-xs text-[#7a6555]">Reward Points</p>
                </div>
                <div className="text-center p-3 bg-[#FDF8F0] rounded-xl">
                  <TrendingUp size={20} className="mx-auto text-[#C4844A] mb-1" />
                  <p className="text-2xl font-bold text-[#2C1810]">{pointsInfo?.multiplier || 1}x</p>
                  <p className="text-xs text-[#7a6555]">Points Multiplier</p>
                </div>
                <div className="text-center p-3 bg-[#FDF8F0] rounded-xl">
                  <ShoppingBag size={20} className="mx-auto text-[#5C7A3A] mb-1" />
                  <p className="text-2xl font-bold text-[#2C1810]">{user.orders_count || 0}</p>
                  <p className="text-xs text-[#7a6555]">Orders</p>
                </div>
                <div className="text-center p-3 bg-[#FDF8F0] rounded-xl">
                  <Award size={20} className="mx-auto text-[#C4844A] mb-1" />
                  <p className="text-2xl font-bold text-[#2C1810]">{'\u20B9'}{pointsInfo?.points_value || 0}</p>
                  <p className="text-xs text-[#7a6555]">Points Value</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Points Info */}
          <Card className="mb-6 border border-[#E8DFD3] shadow-sm">
            <CardContent className="p-5">
              <h3 className="font-bold text-[#2C1810] mb-3 flex items-center gap-2">
                <Gift size={16} className="text-[#5C7A3A]" /> How Points Work
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div className="bg-[#FDF8F0] rounded-xl p-4">
                  <p className="font-semibold text-[#2C1810] mb-1">Earn</p>
                  <p className="text-[#7a6555]">1 point per ₹10 spent, multiplied by your tier ({pointsInfo?.multiplier || 1}x)</p>
                </div>
                <div className="bg-[#FDF8F0] rounded-xl p-4">
                  <p className="font-semibold text-[#2C1810] mb-1">Redeem</p>
                  <p className="text-[#7a6555]">100 points = ₹10 discount at checkout</p>
                </div>
                <div className="bg-[#FDF8F0] rounded-xl p-4">
                  <p className="font-semibold text-[#2C1810] mb-1">Tier Discount</p>
                  <p className="text-[#7a6555]">Use "Apply Membership Discount" at checkout for up to 25% off</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link to="/products">
              <Card className="border border-[#E8DFD3] hover:shadow-md hover:border-[#5C7A3A]/30 transition-all cursor-pointer">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#5C7A3A]/10 rounded-xl flex items-center justify-center">
                    <ShoppingBag size={18} className="text-[#5C7A3A]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#2C1810]">Order Now</p>
                    <p className="text-xs text-[#7a6555]">Browse products & earn points</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/membership">
              <Card className="border border-[#E8DFD3] hover:shadow-md hover:border-[#D4A574]/30 transition-all cursor-pointer">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#D4A574]/10 rounded-xl flex items-center justify-center">
                    <Crown size={18} className="text-[#D4A574]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#2C1810]">Membership Plans</p>
                    <p className="text-xs text-[#7a6555]">View benefits & upgrade</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Account;
